import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { WORD_API_URL } from '../config';
import { profilePath, rememberProfileExit } from '../utils/profileNav';
import { WORD_APP } from '../apps';
import AppShell from './AppShell';
import './Leaderboard.css';

const PERIOD_OPTIONS = [
  { key: 'daily', label: 'Сегодня' },
  { key: 'weekly', label: 'Неделя' },
  { key: 'alltime', label: 'Все время' }
];

const WEEK_RANGE_OPTIONS = [
  { key: 'current', label: 'Эта неделя' },
  { key: 'previous', label: 'Прошлая неделя' }
];

function formatTime(seconds) {
  if (seconds === null || seconds === undefined) return '-';
  const rounded = Math.max(0, Math.round(seconds));
  if (rounded < 60) return `${rounded}с`;
  const minutes = Math.floor(rounded / 60);
  const secs = rounded % 60;
  return `${minutes}м ${secs}с`;
}

function gamesLabel(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} игра`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} игры`;
  return `${n} игр`;
}

function Leaderboard({
  apiUrl = WORD_API_URL,
  app = WORD_APP,
  wordsPerGame = null
}) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [activePeriod, setActivePeriod] = useState('daily');
  const [activeRange, setActiveRange] = useState('current');
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, limit: 50 });
  const [periodInfo, setPeriodInfo] = useState(null);
  const [mineVisible, setMineVisible] = useState(true);
  const mineRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchLeaderboard(activePeriod, activeRange, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePeriod, activeRange]);

  useEffect(() => {
    const node = mineRef.current;
    if (!node) {
      setMineVisible(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setMineVisible(entry.isIntersecting),
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [leaderboard, loading, activePeriod, activeRange, pagination.page]);

  const fetchLeaderboard = async (period, range, page) => {
    setLoading(true);
    setMineVisible(true);
    try {
      const params = { page, limit: 50 };
      if (period === 'weekly') {
        params.range = range;
      }
      const response = await axios.get(`${apiUrl}/leaderboard/${period}`, { params });
      setLeaderboard(response.data.leaderboard);
      setViewer(response.data.viewer || null);
      setPagination(response.data.pagination);
      setPeriodInfo(response.data.periodInfo || null);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setActivePeriod(period);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setActiveRange('current');
  };

  const handleRangeChange = (rangeKey) => {
    setActiveRange(rangeKey);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (direction) => {
    const nextPage = pagination.page + direction;
    if (nextPage < 1 || nextPage > pagination.totalPages) return;
    fetchLeaderboard(activePeriod, activeRange, nextPage);
  };

  const openProfile = (username) => {
    rememberProfileExit(location.pathname);
    navigate(profilePath(username, app?.key));
  };

  const isDaily = periodInfo?.mode === 'current_word' || activePeriod === 'daily';
  const showPin = !loading && viewer && !mineVisible;

  const metaFor = (entry) => {
    if (activePeriod === 'weekly') {
      const played = Number(entry.games_played) || 0;
      return gamesLabel(app?.key === 'dozen' ? Math.min(7, played) : played);
    }
    if (isDaily) {
      return [
        wordsPerGame ? `${entry.solved_count ?? 0}/${wordsPerGame}` : null,
        formatTime(entry.best_time ?? entry.avg_time)
      ]
        .filter(Boolean)
        .join(' · ');
    }
    return `${entry.games_played ?? 0} игр · ср ${formatTime(entry.avg_time)} · лучш ${formatTime(entry.best_time)}`;
  };

  const isMine = (entry) => entry.username === user?.username;

  return (
    <AppShell title="Лидеры" app={app}>
      <div className={`leaderboard-content${showPin ? ' leaderboard-content--pin' : ''}`}>
        <div className="leaderboard-controls">
          <label className="period-select-label" htmlFor="leaderboard-period">
            Период
          </label>
          <select
            id="leaderboard-period"
            className="field period-select"
            value={activePeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {activePeriod === 'weekly' && (
          <div className="sub-tabs">
            {WEEK_RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`sub-tab ${activeRange === option.key ? 'active' : ''}`}
                onClick={() => handleRangeChange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {periodInfo?.displayRange && (
          <div className="period-summary">
            <div className="period-range">{periodInfo.displayRange}</div>
          </div>
        )}

        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : leaderboard.length === 0 ? (
          <div className="empty-state">Пока нет результатов. Сыграйте первым.</div>
        ) : (
          <>
            <div className="leaderboard-list">
              {leaderboard.map((entry, index) => {
                const overallRank = (pagination.page - 1) * pagination.limit + index + 1;
                const mine = isMine(entry);
                return (
                  <button
                    key={entry.id || `${entry.username}-${index}`}
                    type="button"
                    ref={mine ? mineRef : null}
                    className={`leaderboard-item${mine ? ' current-user' : ''}`}
                    onClick={() => openProfile(entry.username)}
                  >
                    <div className={`rank${overallRank <= 3 ? ` rank-${overallRank}` : ''}`}>
                      {overallRank}
                    </div>
                    <div className="user-details">
                      <div className="user-row">
                        <span className="user-name">{entry.username}</span>
                        <span className="total-score">
                          {(entry.total_score || 0).toLocaleString()} очк.
                        </span>
                      </div>
                      <div className="meta-row">
                        <span>{metaFor(entry)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  type="button"
                  onClick={() => handlePageChange(-1)}
                  disabled={pagination.page === 1}
                  className="page-button"
                >
                  ← Назад
                </button>
                <span className="page-info">
                  Страница {pagination.page} из {pagination.totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="page-button"
                >
                  Вперед →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showPin && (
        <button
          type="button"
          className="lb-pin"
          onClick={() => openProfile(viewer.username)}
        >
          <div className="rank">{viewer.rank}</div>
          <div className="user-details">
            <div className="user-row">
              <span className="user-name">{viewer.username}</span>
              <span className="total-score">
                {(viewer.total_score || 0).toLocaleString()} очк.
              </span>
            </div>
          </div>
        </button>
      )}
    </AppShell>
  );
}

export default Leaderboard;
